import { useState } from 'react';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  FileText,
  Package,
  Settings2,
} from 'lucide-react';
import { Badge } from './Badge';
import type { DiffSummaryEntry, RoutineResultEntry, SystemDiff } from '@/hooks/useRuns';

// Re-export, damit Konsumenten nicht zwischen Hook und Komponente
// koordinieren müssen.
export type { DiffSummaryEntry, RoutineResultEntry, SystemDiff };

/**
 * DiffSummaryCard - Aggregat-Anzeige des Dry-Run-Diffs pro System (WT-504).
 *
 * Zeigt 3 Kategorien (Files / Pakete / Services) mit Counter-Badges; Click
 * expandiert die Liste. Lange Diffs werden auf 50 Einträge pro Kategorie
 * gecapped, der Rest steht hinter "Mehr anzeigen" als Collapse.
 *
 * Dazu die Result-Blöcke der Routinen (diff.other): pro Host eine rohe
 * Schluessel/Wert-Liste. Bewusst ohne Deutung - jede Routine druckt ihre
 * eigenen Schlüssel. Nur zwei Befunde bekommen ein Warn-Badge, weil sie
 * Handlung verlangen: gestoppte Dienste und ein ausstehender Neustart.
 */

/** Zahl aus einem Result-Wert lesen; alles Nicht-Zählbare = 0. */
function alsZahl(wert: unknown): number {
  return typeof wert === 'number' && Number.isFinite(wert) ? wert : 0;
}

/**
 * Warnwürdige Befunde in den Result-Blöcken: `dienste_gestoppt_count > 0`
 * oder `reboot_noetig === true`. Alles andere ist Information, keine Warnung.
 */
export function resultWarnungen(entries: readonly RoutineResultEntry[]): {
  diensteGestoppt: number;
  rebootNoetig: boolean;
} {
  let diensteGestoppt = 0;
  let rebootNoetig = false;
  for (const e of entries) {
    diensteGestoppt += alsZahl(e.result['dienste_gestoppt_count']);
    if (e.result['reboot_noetig'] === true) rebootNoetig = true;
  }
  return { diensteGestoppt, rebootNoetig };
}

interface Props {
  diff: SystemDiff;
  /** Max sichtbare Zeilen pro Kategorie vor "weitere n anzeigen". */
  collapseThreshold?: number;
  className?: string;
}

export function DiffSummaryCard({
  diff,
  collapseThreshold = 50,
  className,
}: Props): JSX.Element {
  const other = diff.other ?? [];
  const warnungen = resultWarnungen(other);
  const total =
    diff.changed_files.length +
    diff.changed_packages.length +
    diff.changed_services.length +
    other.length;

  return (
    <section
      className={[
        'rounded-md border border-border bg-surface p-3',
        className ?? '',
      ].join(' ')}
      data-testid={`diff-card-${diff.system_id}`}
    >
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-sm">
          {diff.system_hostname}
          {diff.customer_name && (
            <span className="ml-2 text-xs font-normal text-muted">
              ({diff.customer_name})
            </span>
          )}
        </h3>
        <span className="ml-auto inline-flex flex-wrap gap-1.5">
          {/* Die beiden Befunde, die nicht in einer Schluessel/Wert-Zeile
              untergehen dürfen: da steht Arbeit an, bevor die Nacht endet. */}
          {warnungen.diensteGestoppt > 0 && (
            <Badge tone="warning" data-testid={`diff-warn-dienste-${diff.system_id}`}>
              <span className="inline-flex items-center gap-1">
                <AlertTriangle className="size-3" aria-hidden="true" />
                {warnungen.diensteGestoppt}{' '}
                {warnungen.diensteGestoppt === 1 ? 'Dienst gestoppt' : 'Dienste gestoppt'}
              </span>
            </Badge>
          )}
          {warnungen.rebootNoetig && (
            <Badge tone="warning" data-testid={`diff-warn-reboot-${diff.system_id}`}>
              <span className="inline-flex items-center gap-1">
                <AlertTriangle className="size-3" aria-hidden="true" />
                Neustart nötig
              </span>
            </Badge>
          )}
          <Badge tone={diff.changed_files.length > 0 ? 'primary' : 'neutral'}>
            {diff.changed_files.length} Files
          </Badge>
          <Badge tone={diff.changed_packages.length > 0 ? 'primary' : 'neutral'}>
            {diff.changed_packages.length} Pakete
          </Badge>
          <Badge tone={diff.changed_services.length > 0 ? 'warning' : 'neutral'}>
            {diff.changed_services.length} Services
          </Badge>
        </span>
      </header>

      {total === 0 ? (
        <p className="text-sm text-muted">Keine Änderungen erkannt (Dry-Run sauber).</p>
      ) : (
        <div className="flex flex-col gap-2">
          <DiffSection
            title="Files"
            entries={diff.changed_files}
            icon={<FileText className="size-3" />}
            collapseThreshold={collapseThreshold}
          />
          <DiffSection
            title="Pakete"
            entries={diff.changed_packages}
            icon={<Package className="size-3" />}
            collapseThreshold={collapseThreshold}
          />
          <DiffSection
            title="Services"
            entries={diff.changed_services}
            icon={<Settings2 className="size-3" />}
            collapseThreshold={collapseThreshold}
          />
          {other.map((entry, i) => (
            <RoutineResultBlock key={`${entry.host ?? 'host'}-${i}`} entry={entry} />
          ))}
        </div>
      )}

      {diff.notes && (
        <p className="mt-2 text-xs text-warning">{diff.notes}</p>
      )}
    </section>
  );
}

interface SectionProps {
  title: string;
  entries: DiffSummaryEntry[];
  icon: JSX.Element;
  collapseThreshold: number;
}

function DiffSection({
  title,
  entries,
  icon,
  collapseThreshold,
}: SectionProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  if (entries.length === 0) return null;

  const visible = expanded ? entries : entries.slice(0, collapseThreshold);
  const hasMore = entries.length > collapseThreshold;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-1 rounded-md px-1 py-0.5 text-left text-xs font-medium hover:bg-surface-2"
      >
        {open ? (
          <ChevronDown className="size-3" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-3" aria-hidden="true" />
        )}
        {icon}
        <span>{title}</span>
        <span className="text-muted">({entries.length})</span>
      </button>
      {open && (
        <ul className="mt-1 flex flex-col gap-0.5 border-l border-border pl-3 font-mono text-xs">
          {visible.map((e, i) => (
            <li
              key={`${e.kind}-${e.target}-${i}`}
              className="flex flex-wrap items-center gap-1.5"
            >
              {e.action && <span className="text-muted">[{e.action}]</span>}
              <span className="text-text">{e.target}</span>
              {(e.before || e.after) && (
                <span className="text-muted">
                  {e.before ?? '∅'} → {e.after ?? '∅'}
                </span>
              )}
            </li>
          ))}
          {hasMore && (
            <li>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-xs text-primary hover:underline"
              >
                {expanded
                  ? 'Weniger anzeigen'
                  : `Erweitern (${entries.length - collapseThreshold} weitere)`}
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Result-Wert lesbar machen; verschachtelte Strukturen roh als JSON. */
function wertText(wert: unknown): string {
  if (wert === null || wert === undefined) return '–';
  if (typeof wert === 'string') return wert;
  if (typeof wert === 'number' || typeof wert === 'boolean') return String(wert);
  try {
    // JSON.stringify liefert für Funktionen/Symbole undefined - und
    // "[object Object]" wäre schlechter als ein ehrlicher Platzhalter.
    return JSON.stringify(wert) ?? '(nicht darstellbar)';
  } catch {
    return '(nicht darstellbar)';
  }
}

/**
 * Ein geernteter Result-Block, roh als Schluessel/Wert-Liste pro Host.
 *
 * Aufgeklappt als Default - anders als die Kategorien oben ist das hier oft
 * der EINZIGE Inhalt des Diffs (Client-Runs), und zugeklappt sähe der Tab
 * wieder leer aus.
 */
function RoutineResultBlock({ entry }: { entry: RoutineResultEntry }): JSX.Element {
  const zeilen = Object.entries(entry.result);
  return (
    <section
      className="rounded-md border border-border bg-surface-2/40 p-2"
      data-testid={`diff-result-block-${entry.host ?? 'unbekannt'}`}
    >
      <h4 className="flex flex-wrap items-center gap-2 text-xs font-medium">
        <span className="font-mono">{entry.host ?? 'Host unbekannt'}</span>
        {entry.routine && <Badge tone="neutral">{entry.routine}</Badge>}
        <span className="text-muted">Ergebnis der Routine</span>
      </h4>
      {zeilen.length === 0 ? (
        <p className="mt-1 text-xs text-muted">Leerer Result-Block.</p>
      ) : (
        <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 font-mono text-xs">
          {zeilen.map(([schluessel, wert]) => (
            <div key={schluessel} className="contents">
              <dt className="text-muted">{schluessel}</dt>
              <dd className="break-words">{wertText(wert)}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
